import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { Field, FieldLabel } from '@ordo/ui/field'
import { Input } from '@ordo/ui/input'
import { useEffect, useState } from 'react'

/**
 * Plan 50 — `/set-password?token=…`. Consumes the multipass
 * `set_password` token issued by `POST /api/service/users/password-reset`
 * (Phase 4 endpoint). On success the user can return to `/signin` and
 * sign in with the new password.
 *
 * The form POSTs to our own `/api/auth/password/set`, which proxies
 * multipass server-side. It must NOT call multipass directly from the
 * browser: multipass serves no CORS headers, so a cross-origin POST is
 * blocked (PROD-206).
 */
export function SetPasswordPage() {
  const [token, setToken] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const params = new URLSearchParams(window.location.search)
    setToken(params.get('token'))
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting || !token) {
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    setError(null)
    const res = await fetch('/api/auth/password/set', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, password }),
    })
    setSubmitting(false)
    if (res.ok) {
      setStatus('success')
    } else {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      setStatus('error')
      setError(body.error ?? 'Could not set password. The link may have expired.')
    }
  }

  if (token === null && typeof window !== 'undefined') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="flex w-full max-w-sm flex-col gap-6 p-8 shadow-md">
          <h1 className="text-center text-2xl font-semibold tracking-tight">Invalid link</h1>
          <p className="text-center text-sm text-muted-foreground">
            This password-reset link is missing its token. Request a new one from{' '}
            <a href="/forgot-password" className="underline">
              forgot password
            </a>
            .
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="flex w-full max-w-sm flex-col gap-6 p-8 shadow-md">
        <header className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Set password</h1>
          <p className="mt-1 text-sm text-muted-foreground">Choose a password for your Ordo account.</p>
        </header>

        {error ? (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {status !== 'success' ? (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <Field>
              <FieldLabel htmlFor="set-password">New password</FieldLabel>
              <Input
                id="set-password"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="set-password-confirm">Confirm password</FieldLabel>
              <Input
                id="set-password-confirm"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>
            <Button type="submit" disabled={submitting || !token}>
              {submitting ? 'Saving…' : 'Set password'}
            </Button>
          </form>
        ) : (
          <div className="rounded-md border border-border bg-muted/40 p-4 text-center text-sm">
            <p className="font-medium">Password set</p>
            <p className="mt-1 text-muted-foreground">You can now sign in with your new password.</p>
            <a href="/signin" className="mt-3 inline-block text-xs text-foreground hover:underline">
              Go to sign in
            </a>
          </div>
        )}
      </Card>
    </div>
  )
}
